import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './HighJumpSettings.module.css';
import { Plus, ChevronDown, ChevronRight } from 'lucide-react';
import CustomNewButton from 'components/NewDesign/CustomNewButton';

// showMinimumHeights: false for a club that groups by year level. A minimum starting height belongs to an
// athlete's age, and there is no data on what schools do (Doug, 2026-10-11), so that section and its tip are
// not shown and only the elimination rule is. The settings it holds are passed through unchanged.
const HighJumpSettings = ({ highJumpSettings, onChange, showMinimumHeights = true }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [eliminationMode, setEliminationMode] = useState('consecutiveMisses');
  const [maxMisses, setMaxMisses] = useState(3);
  const [minimumStartHeights, setMinimumStartHeights] = useState([]);

  // Initialize from props - map API format to UI format
  useEffect(() => {
    if (highJumpSettings) {
      setEliminationMode(highJumpSettings.eliminationMode || 'consecutiveMisses');
      setMaxMisses(highJumpSettings.maxMisses || 3);

      // Map from API format (age, f, m) to UI format (ageRange, femaleHeight, maleHeight)
      const mappedHeights = (highJumpSettings.minimumStartHeights || []).map(height => ({
        ageRange: height.age || '',
        femaleHeight: (height.f !== undefined && height.f !== null && height.f !== 0)
          ? Number(height.f).toFixed(2)
          : '',
        maleHeight: (height.m !== undefined && height.m !== null && height.m !== 0)
          ? Number(height.m).toFixed(2)
          : '',
      }));

      setMinimumStartHeights(mappedHeights);
    }
  }, [highJumpSettings]);

  // Notify parent of changes - map UI format to API format
  const notifyChange = useCallback((mode, misses, heights) => {
    if (onChange) {
      const mappedHeights = heights.map(height => ({
        age: height.ageRange || '',
        f: height.femaleHeight !== '' && height.femaleHeight !== null && height.femaleHeight !== undefined
          ? parseFloat(height.femaleHeight)
          : null,
        m: height.maleHeight !== '' && height.maleHeight !== null && height.maleHeight !== undefined
          ? parseFloat(height.maleHeight)
          : null,
      }));

      onChange({
        eliminationMode: mode,
        maxMisses: misses,
        minimumStartHeights: mappedHeights,
      });
    }
  }, [onChange]);

  // Handle elimination mode change
  const handleEliminationModeChange = useCallback((e) => {
    const newMode = e.target.value;
    setEliminationMode(newMode);
    notifyChange(newMode, maxMisses, minimumStartHeights);
  }, [maxMisses, minimumStartHeights, notifyChange]);

  // Handle max misses change
  const handleMaxMissesChange = useCallback((e) => {
    const newMaxMisses = parseInt(e.target.value, 10) || 3;
    setMaxMisses(newMaxMisses);
    notifyChange(eliminationMode, newMaxMisses, minimumStartHeights);
  }, [eliminationMode, minimumStartHeights, notifyChange]);

  // Handle height field changes (local state only, doesn't notify parent)
  const handleHeightChange = useCallback((index, field, value) => {
    const updatedHeights = [...minimumStartHeights];
    updatedHeights[index] = {
      ...updatedHeights[index],
      [field]: value,
    };
    setMinimumStartHeights(updatedHeights);
  }, [minimumStartHeights]);

  // Handle height field blur (notifies parent with final value)
  const handleHeightBlur = useCallback((index, field, value) => {
    const updatedHeights = [...minimumStartHeights];
    updatedHeights[index] = {
      ...updatedHeights[index],
      [field]: value,
    };
    notifyChange(eliminationMode, maxMisses, updatedHeights);
  }, [eliminationMode, maxMisses, minimumStartHeights, notifyChange]);

  // Add new age group row
  const handleAddAge = useCallback(() => {
    const newHeights = [
      ...minimumStartHeights,
      { ageRange: '', femaleHeight: '', maleHeight: '' },
    ];
    setMinimumStartHeights(newHeights);
    notifyChange(eliminationMode, maxMisses, newHeights);
  }, [eliminationMode, maxMisses, minimumStartHeights, notifyChange]);

  // Remove age group row
  const handleRemoveAge = useCallback((index) => {
    const newHeights = minimumStartHeights.filter((_, i) => i !== index);
    setMinimumStartHeights(newHeights);
    notifyChange(eliminationMode, maxMisses, newHeights);
  }, [eliminationMode, maxMisses, minimumStartHeights, notifyChange]);

  return (
    <div className={styles.container}>
      <div 
        className={styles.header}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className={styles.headerContent}>
          {isCollapsed ? (
            <ChevronRight size={20} />
          ) : (
            <ChevronDown size={20} />
          )}
          <h3 className={styles.title}>High Jump Settings</h3>
          <span className={styles.subtitle}>
            {showMinimumHeights
              ? 'Configure elimination rules and minimum starting heights'
              : 'Configure elimination rules'}
          </span>
        </div>
      </div>

      {!isCollapsed && (
        <div className={styles.content}>
          {/* Elimination Rules Section */}
          <div className={styles.rulesSection}>
            <div className={styles.rulesRow}>
              <label className={styles.label}>Elimination Rule</label>
              <select
                className={styles.rulesSelect}
                value={eliminationMode}
                onChange={handleEliminationModeChange}
              >
                <option value="consecutiveMisses">3 consecutive misses</option>
                <option value="totalMisses">Consecutive OR total misses</option>
              </select>
              {eliminationMode === 'totalMisses' && (
                <>
                  <span className={styles.labelText}>eliminate after 3 consecutive or</span>
                  <input
                    type="number"
                    className={styles.numberInput}
                    min="1"
                    max="10"
                    value={maxMisses}
                    onChange={handleMaxMissesChange}
                  />
                  <span className={styles.labelText}>total misses</span>
                </>
              )}
            </div>
          </div>

          {/* Minimum Starting Heights Section */}
          {showMinimumHeights && (
          <div className={styles.heightsSection}>
            <h4 className={styles.sectionTitle}>Minimum starting heights (meters)</h4>
            
            {minimumStartHeights.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No minimum starting heights configured. Click &quot;Add Age&quot; to add one.</p>
              </div>
            ) : (
              <div className={styles.heightsTable}>
                <div className={styles.tableHeader}>
                  <div className={styles.headerCell}>Age Range</div>
                  <div className={styles.headerCell}>Female Height (m)</div>
                  <div className={styles.headerCell}>Male Height (m)</div>
                  <div className={styles.headerCell}></div>
                </div>
                
                {minimumStartHeights.map((height, index) => (
                  <div key={index} className={styles.tableRow}>
                    <div className={styles.tableCell}>
                      <input
                        type="text"
                        className={styles.textInput}
                        placeholder="e.g., 6, 8-12"
                        value={height.ageRange || ''}
                        onChange={(e) => {
                          let value = e.target.value;
                          
                          // Allow empty value
                          if (value === '') {
                            handleHeightChange(index, 'ageRange', '');
                            return;
                          }
                          
                          // Only allow digits and one hyphen
                          // Remove any characters that aren't digits or hyphen
                          value = value.replace(/[^\d-]/g, '');
                          
                          // Only allow one hyphen
                          const hyphenCount = (value.match(/-/g) || []).length;
                          if (hyphenCount > 1) {
                            return; // Don't update if multiple hyphens
                          }
                          
                          // Don't allow hyphen at start or end
                          if (value.startsWith('-') || value.endsWith('-')) {
                            // Allow typing hyphen in the middle, just don't allow it to stay at ends
                            if (value.endsWith('-') && value.length > 1) {
                              handleHeightChange(index, 'ageRange', value);
                            }
                            return;
                          }
                          
                          // If there's a hyphen, validate the range
                          if (value.includes('-')) {
                            const parts = value.split('-');
                            // Each part should be a valid number
                            if (parts[0] && parts[1]) {
                              const start = parseInt(parts[0], 10);
                              const end = parseInt(parts[1], 10);
                              // Limit ages to reasonable range (5-99)
                              if (start > 99 || end > 99) {
                                return;
                              }
                            }
                          } else {
                            // Single number - limit to reasonable age
                            const age = parseInt(value, 10);
                            if (age > 99) {
                              return;
                            }
                          }
                          
                          handleHeightChange(index, 'ageRange', value);
                        }}
                        onBlur={(e) => {
                          let value = e.target.value;
                          if (value === '') return;
                          
                          // Clean up on blur
                          // Remove trailing/leading hyphens
                          value = value.replace(/^-+|-+$/g, '');
                          
                          // If it's a range, ensure start < end
                          if (value.includes('-')) {
                            const parts = value.split('-');
                            if (parts[0] && parts[1]) {
                              const start = parseInt(parts[0], 10);
                              const end = parseInt(parts[1], 10);
                              if (start > end) {
                                // Swap them
                                value = `${end}-${start}`;
                              }
                            }
                          }
                          
                          handleHeightChange(index, 'ageRange', value);
                        }}
                      />
                    </div>
                    <div className={styles.tableCell}>
                      <input
                        type="text"
                        inputMode="decimal"
                        className={styles.textInput}
                        placeholder="0.00"
                        value={height.femaleHeight !== undefined && height.femaleHeight !== null ? height.femaleHeight : ''}
                        onChange={(e) => {
                          let value = e.target.value;
                          
                          // Allow empty value
                          if (value === '') {
                            handleHeightChange(index, 'femaleHeight', '');
                            return;
                          }
                          
                          // Remove any non-digit and non-decimal characters
                          value = value.replace(/[^\d.]/g, '');
                          
                          // Only allow one decimal point
                          const parts = value.split('.');
                          if (parts.length > 2) {
                            return; // Don't update if multiple decimals
                          }
                          
                          // Limit to 2 decimal places
                          if (parts.length === 2 && parts[1].length > 2) {
                            value = parts[0] + '.' + parts[1].substring(0, 2);
                          }
                          
                          // Don't allow leading zeros before non-decimal numbers (e.g., 05)
                          if (parts[0].length > 1 && parts[0][0] === '0') {
                            parts[0] = parts[0].replace(/^0+/, '0');
                            value = parts.join('.');
                          }
                          
                          // Limit integer part to reasonable value (e.g., max 9.99 meters)
                          if (parts[0] && parseFloat(parts[0]) > 9) {
                            return; // Don't allow more than 9 meters
                          }
                          
                          handleHeightChange(index, 'femaleHeight', value);
                        }}
                        onBlur={(e) => {
                          let value = e.target.value;
                          if (value === '') {
                            handleHeightBlur(index, 'femaleHeight', '');
                            return;
                          }

                          // Format on blur: add leading zero if starts with decimal
                          if (value.startsWith('.')) {
                            value = '0' + value;
                          }

                          // Ensure 2 decimal places
                          const num = parseFloat(value);
                          if (!isNaN(num)) {
                            value = num.toFixed(2);
                            handleHeightChange(index, 'femaleHeight', value);
                            handleHeightBlur(index, 'femaleHeight', value);
                          }
                        }}
                      />
                    </div>
                    <div className={styles.tableCell}>
                      <input
                        type="text"
                        inputMode="decimal"
                        className={styles.textInput}
                        placeholder="0.00"
                        value={height.maleHeight !== undefined && height.maleHeight !== null ? height.maleHeight : ''}
                        onChange={(e) => {
                          let value = e.target.value;
                          
                          // Allow empty value
                          if (value === '') {
                            handleHeightChange(index, 'maleHeight', '');
                            return;
                          }
                          
                          // Remove any non-digit and non-decimal characters
                          value = value.replace(/[^\d.]/g, '');
                          
                          // Only allow one decimal point
                          const parts = value.split('.');
                          if (parts.length > 2) {
                            return; // Don't update if multiple decimals
                          }
                          
                          // Limit to 2 decimal places
                          if (parts.length === 2 && parts[1].length > 2) {
                            value = parts[0] + '.' + parts[1].substring(0, 2);
                          }
                          
                          // Don't allow leading zeros before non-decimal numbers (e.g., 05)
                          if (parts[0].length > 1 && parts[0][0] === '0') {
                            parts[0] = parts[0].replace(/^0+/, '0');
                            value = parts.join('.');
                          }
                          
                          // Limit integer part to reasonable value (e.g., max 9.99 meters)
                          if (parts[0] && parseFloat(parts[0]) > 9) {
                            return; // Don't allow more than 9 meters
                          }
                          
                          handleHeightChange(index, 'maleHeight', value);
                        }}
                        onBlur={(e) => {
                          let value = e.target.value;
                          if (value === '') {
                            handleHeightBlur(index, 'maleHeight', '');
                            return;
                          }

                          // Format on blur: add leading zero if starts with decimal
                          if (value.startsWith('.')) {
                            value = '0' + value;
                          }

                          // Ensure 2 decimal places
                          const num = parseFloat(value);
                          if (!isNaN(num)) {
                            value = num.toFixed(2);
                            handleHeightChange(index, 'maleHeight', value);
                            handleHeightBlur(index, 'maleHeight', value);
                          }
                        }}
                      />
                    </div>
                    <div className={styles.tableCell} style={{ justifyContent: 'center' }}>
                      <CustomNewButton
                        onClick={() => handleRemoveAge(index)}
                        customStyle={{
                          backgroundColor: '#fee2e2',
                          color: '#dc2626',
                          border: '1px solid #fca5a5',
                          padding: '6px',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          fontSize: '18px',
                          fontWeight: 'bold',
                        }}
                      >
                        ×
                      </CustomNewButton>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.addButtonRow}>
              <CustomNewButton
                onClick={handleAddAge}
                variant="outline"
                customStyle={{
                  backgroundColor: '#4a90e2',
                  color: 'white',
                  border: '1px solid #4a90e2',
                  padding: '8px 16px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <Plus size={16} />
                Add Age
              </CustomNewButton>
            </div>
          </div>
          )}

          {/* Help Text */}
          {showMinimumHeights && (
          <div className={styles.helpText}>
            <p>
              <strong>💡 Tip:</strong> Set age ranges like &quot;6&quot; for a single age or
              &quot;8-12&quot; for a range.
              Heights are in meters (e.g., 0.85 for 85cm).
            </p>
          </div>
          )}
        </div>
      )}
    </div>
  );
};

HighJumpSettings.propTypes = {
  highJumpSettings: PropTypes.shape({
    eliminationMode: PropTypes.string,
    maxMisses: PropTypes.number,
    minimumStartHeights: PropTypes.arrayOf(
      PropTypes.shape({
        age: PropTypes.string,
        f: PropTypes.number,
        m: PropTypes.number,
      })
    ),
  }),
  onChange: PropTypes.func,
  showMinimumHeights: PropTypes.bool,
};

export default HighJumpSettings;
